import type { Contact } from "../types";

/**
 * Per-recipient contact variables usable inside template parameter VALUES as
 * `{{contact.<name>|fallback}}` tokens. WhatsApp's own placeholders stay
 * numeric (`{{1}}`); only our values carry tokens, resolved server-side per
 * recipient at audience-expansion time. This registry mirrors the backend's
 * BROADCAST_CONTACT_VARIABLES (dashboard-api) — keep the two in sync.
 */
export type ContactTemplateVariableName =
  | "portal_url"
  | "rated_echo_title"
  | "latest_echo_title"
  | "first_name"
  | "last_rating";

export type ContactTemplateVariableOption = {
  name: ContactTemplateVariableName;
  labelKey: string;
  /** Inserted into the token so nobody is skipped unless the operator opts in. */
  defaultFallback: string;
};

export const CONTACT_TEMPLATE_VARIABLES: ContactTemplateVariableOption[] = [
  {
    name: "portal_url",
    labelKey: "crm.whatsapp.broadcasts.variables.portal_url",
    defaultFallback: "https://my.echoflicks.com/login",
  },
  {
    name: "rated_echo_title",
    labelKey: "crm.whatsapp.broadcasts.variables.rated_echo_title",
    defaultFallback: "your latest Echo",
  },
  {
    name: "latest_echo_title",
    labelKey: "crm.whatsapp.broadcasts.variables.latest_echo_title",
    defaultFallback: "your latest Echo",
  },
  {
    name: "first_name",
    labelKey: "crm.whatsapp.broadcasts.variables.first_name",
    defaultFallback: "there",
  },
  {
    name: "last_rating",
    labelKey: "crm.whatsapp.broadcasts.variables.last_rating",
    defaultFallback: "",
  },
];

const TOKEN_PATTERN = /\{\{\s*contact\.([a-z0-9_]+)\s*(?:\|([^{}]*))?\}\}/g;

export function contactVariableToken(
  name: ContactTemplateVariableName,
  fallback?: string,
): string {
  const trimmed = (fallback ?? "").trim();
  return trimmed ? `{{contact.${name}|${trimmed}}}` : `{{contact.${name}}}`;
}

function contactVariableValue(
  name: string,
  contact: Partial<Contact>,
): string | null {
  switch (name) {
    case "portal_url":
      return "https://my.echoflicks.com/login"; // Actual recipient-bound URL is generated server-side.
    case "rated_echo_title":
      return textOrNull(contact.last_rated_echo_title);
    case "latest_echo_title":
      return textOrNull(contact.last_echo_title);
    case "first_name": {
      const first = textOrNull(contact.first_name);
      if (first) return first;
      const full = textOrNull(
        (contact as { contact_name?: string | null }).contact_name ??
          (contact as { name?: string | null }).name,
      );
      return full ? full.split(/\s+/)[0] : null;
    }
    case "last_rating":
      return contact.last_rating != null ? String(contact.last_rating) : null;
    default:
      return null;
  }
}

function textOrNull(value: unknown): string | null {
  const text = typeof value === "string" ? value.trim() : "";
  return text || null;
}

/**
 * Client-side token resolution — demo/fakerest parity with the backend
 * resolver. Unresolvable tokens without a fallback keep their literal form so
 * the operator can see what is still unresolved.
 */
export function resolveContactVariableTokens(
  value: string,
  contact: Partial<Contact>,
): string {
  return value.replace(
    TOKEN_PATTERN,
    (whole, name: string, fallback?: string) => {
      const resolved = contactVariableValue(name, contact);
      if (resolved) return resolved;
      const trimmed = (fallback ?? "").trim();
      return trimmed || whole;
    },
  );
}

/**
 * Preview-friendly rendering for the composer's fallback mock-up (before any
 * per-recipient values exist): tokens display as their fallback text, or as
 * `⟨name⟩` when they have none.
 */
export function describeVariableTokens(value: string): string {
  return value.replace(
    TOKEN_PATTERN,
    (_whole, name: string, fallback?: string) => {
      const trimmed = (fallback ?? "").trim();
      return trimmed || `⟨${name}⟩`;
    },
  );
}

export function hasContactVariableTokens(value: string): boolean {
  TOKEN_PATTERN.lastIndex = 0;
  return TOKEN_PATTERN.test(value);
}
